import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from '../AppContext';
import { featureFlags } from '../../Services/FeatureFlagService';

import {
  getFeatureFlag,
  getInitialFeatureFlags,
  initializeFeatureFlagService,
} from './utils';

const { NEW_UNITS, QUOTE_UPCHARGES } = featureFlags;

const initialFeatureFlags = {
  isAdminAiAgentsEnabled: null,
  isAdminAssignedManagerEnabled: null,
  isAffordableLeasingEnabled: null,
  isAiTemplatesEnabled: null,
  isApplicantCallRoutingEnabled: null,
  isAdminGoogleSpreadsheetEnabled: null,
  isLeasingTeamTodoThresholdsEnabled: false,
  isAdminLROSettingsEnabled: false,
  isAdminPhotosTabEnabled: false,
  isNewPropertiesPageEnabled: null,
  isPropertiesTourHoursEnabled: false,
  isAdminRequireShowUnitsEnabled: null,
  isAiEmailGenAiAdminEnabled: false,
  isAiEmailTriggerEnabled: null,
  isAiNurtureEnabled: null,
  isAiResponseCountsAsEngagementEnabled: null,
  isBatchSourceCostEnabled: false,
  isBooksMappingEnabled: null,
  isCallIntelStandaloneEnabled: false,
  isClawAIGuestCardSummaryEnabled: false,
  isClawInboxUnreadMessageBadgeEnabled: false,
  isCompanySourceListEnabled: null,
  isCompanyTemplatesEnabled: null,
  isCompanyTemplatesPreviewEnabled: null,
  isCompanyTemplatesSelfGuidedToursEnabled: null,
  isCompanyTemplatesWinbackEnabled: null,
  isConfigurableStaleTriggerEnabled: null,
  isDoorwayDniEnabled: false,
  isDoorwayEnhancementsEnabled: null,
  isDoorwayFeeDisclaimerEnabled: null,
  isDoorwayV2FeeCalculatorEnabled: false,
  isMultiPhasePropertyEnabled: false,
  isDripSmsEnabled: null,
  isEnhancedBrandContentEnabled: null,
  isEnhancedVoiceSettingsEnabled: null,
  isFeeTransparencyEnabled: false,
  isUnifiedLeasingSettingsEnabled: null,
  isUnifiedResidentSettingsEnabled: null,
  isLoftChatDeeplinkEnabled: false,
  isOmnibarEnabled: null,
  isOmnibarHelpEnabled: null,
  isOmnibarUnifiedSupportEnabled: null,
  isOSMEnabled: false,
  isReformedSyndicationEnabled: null,
  isResidentAiNurtureEnabled: null,
  isResidentDripCampaignEnabled: false,
  isSSDAPageEnabled: false,
  isShowNewVAConfigurationUiEnabled: false,
  isSourcesMarketingSourceCostEnabled: false,
  isToursSiteMapEnabled: null,
  isUseCookieForTokenEnabled: false,
  isUserDeleteDisabled: null,
  newUnitsFlags: {},
  renterAIFlag: null,
  renterAIResidentsEmailEnabledProperties: null,
  renterAIResidentsFlag: null,
  quoteUpchargesFlags: {},
};

export const defaultFeatureFlagContext = {
  ...initialFeatureFlags,
  updateNewUnitsFlags: () => {},
  updateQuoteUpchargesFlags: () => {},
};

export const FeatureFlagContext = createContext(defaultFeatureFlagContext);

export const FeatureFlagProvider = ({ children }) => {
  const [featureFlags, setFeatureFlags] = useState(initialFeatureFlags);
  const { companyId, setIsReady } = useAppContext();

  const setInitialFeatureFlags = useCallback(async () => {
    await initializeFeatureFlagService(companyId, companyId);

    const flagValues = await getInitialFeatureFlags();

    setFeatureFlags((prevState) => ({
      ...prevState,
      ...flagValues,
    }));

    setIsReady(true);
  }, [companyId, setFeatureFlags, setIsReady]);

  useEffect(() => {
    if (companyId) {
      setInitialFeatureFlags();
    }
  }, [companyId, setInitialFeatureFlags]);

  const updateScopedFeatureFlag = async (scope, featureFlagName) => {
    await initializeFeatureFlagService(scope, companyId);

    const [flagKey, flagValue] = await getFeatureFlag(featureFlagName);

    setFeatureFlags((prevState) => ({
      ...prevState,
      [flagKey]: {
        ...prevState[flagKey],
        [scope]: flagValue,
      },
    }));
  };

  const updateNewUnitsFlags = (leasingTeamId) => {
    updateScopedFeatureFlag(leasingTeamId, NEW_UNITS);
  };

  const updateQuoteUpchargesFlags = (leasingTeamId) => {
    updateScopedFeatureFlag(leasingTeamId, QUOTE_UPCHARGES);
  };

  const featureFlagContextValue = {
    ...featureFlags,
    updateNewUnitsFlags,
    updateQuoteUpchargesFlags,
  };

  return (
    <FeatureFlagContext.Provider value={featureFlagContextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

FeatureFlagProvider.propTypes = {
  children: PropTypes.node,
};

export const useFeatureFlagContext = () => useContext(FeatureFlagContext);
